if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.form.querySelector('[name=id]').disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        // Was this submit triggered by the auto-recovery on page load? Consume the
        // flag immediately so it only applies to this one submit (see the IIFE at the
        // bottom of this file). Used to decide between the first and second message.
        const isAutoAdd = window.__ppAutoAddInProgress === true;
        window.__ppAutoAddInProgress = false;

        this.handleErrorMessage();
        if (this.successWrapper) this.successWrapper.setAttribute('hidden', ''); // clear any prior success note
        this.error = false;

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        // ===========================================================================
        // Petit Playa resilience (2026-06-30, Claude): TRANSIENT add-to-cart failures
        // were silently swallowed. The stock handler did `fetch().then().catch(console
        // .error)`, so when /cart/add was slow/blocked/throttled (503/429) or dropped
        // — common under the store's elevated cart latency, on VPNs, mobile data, and
        // in-app browsers — the spinner just reverted with NOTHING added and NO message.
        // We now (1) retry transient failures up to 3 times with backoff, and (2) if all
        // retries fail, show a recovery message with a "Click here" link that remembers
        // the size, refreshes the page (the known reset), and auto-adds it on reload.
        // A genuine Shopify cart error (response.status — e.g. truly sold out) is NOT
        // retried; it shows the normal message immediately, exactly as before. The
        // success path is unchanged, so a healthy add behaves identically.
        // ===========================================================================
        const MAX_ATTEMPTS = 3;

        const attempt = (tries) =>
          fetch(`${routes.cart_add_url}`, config)
            .then((response) => {
              // Non-OK HTTP (429/430/503/5xx/etc.) is transient — throw so we retry.
              // These responses are typically not JSON, so we must check before parsing.
              if (!response.ok) {
                throw new Error('Cart add HTTP ' + response.status);
              }
              return response.json();
            })
            .then((response) => {
              if (response.status) {
                publish(PUB_SUB_EVENTS.cartError, {
                  source: 'product-form',
                  productVariantId: formData.get('id'),
                  errors: response.errors || response.description,
                  message: response.message,
                });
                this.handleErrorMessage(response.description);

                const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
                if (!soldOutMessage) return;
                this.submitButton.setAttribute('aria-disabled', true);
                this.submitButton.querySelector('span').classList.add('hidden');
                soldOutMessage.classList.remove('hidden');
                this.error = true;
                return;
              } else if (!this.cart) {
                window.location = window.routes.cart_url;
                return;
              }

              if (!this.error)
                publish(PUB_SUB_EVENTS.cartUpdate, {
                  source: 'product-form',
                  productVariantId: formData.get('id'),
                  cartData: response,
                });
              this.error = false;
              // If this add was the auto-recovery after a "Click here" refresh, the cart
              // drawer doesn't pop on reload, so confirm success inline with a green tick.
              if (isAutoAdd) this.showSuccessMessage(response);
              const quickAddModal = this.closest('quick-add-modal');
              if (quickAddModal) {
                document.body.addEventListener(
                  'modalClosed',
                  () => {
                    setTimeout(() => {
                      this.cart.renderContents(response);
                    });
                  },
                  { once: true }
                );
                quickAddModal.hide(true);
              } else {
                this.cart.renderContents(response);
              }
            })
            .catch((e) => {
              // Transient failure (dropped fetch, 429/503, timeout): retry with backoff.
              if (tries < MAX_ATTEMPTS - 1) {
                return new Promise((resolve) => setTimeout(resolve, 500 * (tries + 1))).then(() =>
                  attempt(tries + 1)
                );
              }
              // Out of retries — show the recovery message (with the Click-here link).
              // We do NOT set this.error here, so the .finally re-enables the button.
              console.error(e);
              this.showRecoveryMessage(formData.get('id'), isAutoAdd);
            });

        attempt(0).finally(() => {
          this.submitButton.classList.remove('loading');
          if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
          if (!this.error) this.submitButton.removeAttribute('aria-disabled');
          this.querySelector('.loading__spinner').classList.add('hidden');
        });
      }

      // Build a recovery message with a bold+underlined "Click here" link.
      // stageTwo === false  -> first failure (a normal click): offer the auto-fix.
      // stageTwo === true   -> the auto-fix itself just failed: offer refresh + contact.
      showRecoveryMessage(variantId, stageTwo) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');
        if (!this.errorMessage) return;

        this.errorMessageWrapper.toggleAttribute('hidden', false);

        const makeLink = (text, onClick, href) => {
          const a = document.createElement('a');
          a.textContent = text;
          a.href = href || '#';
          a.style.color = 'inherit'; // match surrounding message text — only bold + underline
          a.style.fontWeight = '700';
          a.style.textDecoration = 'underline';
          a.style.cursor = 'pointer';
          if (onClick) {
            a.addEventListener('click', (ev) => {
              ev.preventDefault();
              onClick();
            });
          }
          return a;
        };

        const el = this.errorMessage;
        el.textContent = '';

        if (!stageTwo) {
          el.appendChild(document.createTextNode('Sorry, that failed to load. '));
          el.appendChild(makeLink('Click here', () => this.queueRecoveryAdd(variantId)));
          el.appendChild(document.createTextNode(" and we'll fix it for you."));
        } else {
          el.appendChild(document.createTextNode('Hmm still not working. '));
          el.appendChild(makeLink('Click here', () => this.queueRecoveryAdd(variantId)));
          el.appendChild(document.createTextNode(' to refresh your page and add the item again or '));
          el.appendChild(makeLink('contact us', null, '/pages/contact'));
          el.appendChild(document.createTextNode(' for assistance.'));
        }
      }

      // Green-tick confirmation after the auto-recovery add (post "Click here" refresh),
      // since the cart drawer doesn't open on reload. Icon mirrors the theme's red error
      // circle (13x13) but green with a white checkmark. Size name is pulled from the
      // add response so it reads e.g. "Size 4-5Y added to your cart successfully!".
      showSuccessMessage(response) {
        let sizeName = '';
        if (response) {
          if (response.variant_title) sizeName = response.variant_title;
          else if (response.options_with_values && response.options_with_values.length)
            sizeName = response.options_with_values.map((o) => o.value).join(' / ');
          else if (response.title && response.title.indexOf(' - ') > -1)
            sizeName = response.title.split(' - ').pop();
        }

        const errWrap =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        const parent = errWrap ? errWrap.parentElement : this;
        if (!parent) return;

        if (!this.successWrapper) {
          const w = document.createElement('div');
          w.className = 'product-form__error-message-wrapper pp-success-message-wrapper';
          w.setAttribute('role', 'status');
          w.innerHTML =
            '<svg aria-hidden="true" focusable="false" class="icon" viewBox="0 0 13 13" style="margin-right:0.5rem;flex:0 0 auto;">' +
            '<circle cx="6.5" cy="6.50049" r="5.5" stroke="white" stroke-width="2"></circle>' +
            '<circle cx="6.5" cy="6.5" r="5.5" fill="#2E8540" stroke="#2E8540" stroke-width="0.7"></circle>' +
            '<path d="M3.6 6.7 L5.5 8.6 L9.3 4.2" fill="none" stroke="white" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"></path>' +
            '</svg><span class="pp-success-text"></span>';
          if (errWrap && errWrap.nextSibling) parent.insertBefore(w, errWrap.nextSibling);
          else parent.appendChild(w);
          this.successWrapper = w;
        }

        this.successWrapper.querySelector('.pp-success-text').textContent = sizeName
          ? 'Size ' + sizeName + ' added to your cart successfully!'
          : 'Added to your cart successfully!';
        if (errWrap) errWrap.setAttribute('hidden', ''); // hide the error/recovery message
        this.successWrapper.removeAttribute('hidden'); // show the success note
      }

      // Remember the size, then reload to that variant. The IIFE at the bottom of this
      // file auto-adds it once on reload (refresh is the known reset for this glitch).
      queueRecoveryAdd(variantId) {
        try {
          const qtyInput = this.form.querySelector('[name="quantity"]');
          const qty = qtyInput ? parseInt(qtyInput.value) || 1 : 1;
          sessionStorage.setItem(
            'pp_pendingAdd',
            JSON.stringify({ id: variantId, qty: qty, ts: Date.now() })
          );
        } catch (e) {
          /* sessionStorage unavailable — fall back to a plain reload below */
        }
        const base = window.location.pathname;
        window.location.href = base + (variantId ? '?variant=' + variantId : '');
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }
    }
  );
}

// ===========================================================================
// Petit Playa auto-recovery (2026-06-30, Claude): when a shopper clicks the
// "Click here" recovery link, we store the variant and reload to it; on load we
// auto-add it ONCE. Refreshing is the known reset for the transient add glitch,
// so this combines the reset with the re-add — the shopper only clicks once.
// Safeguards: one-shot (cleared before we try, so it can never loop), and it
// expires after 2 minutes so a normal later visit never auto-adds unexpectedly.
// ===========================================================================
(function () {
  function tryPendingAdd() {
    let pending = null;
    try {
      pending = JSON.parse(sessionStorage.getItem('pp_pendingAdd') || 'null');
    } catch (e) {
      pending = null;
    }
    if (!pending || !pending.id) return;
    // Expire stale requests.
    if (!pending.ts || Date.now() - pending.ts > 120000) {
      try {
        sessionStorage.removeItem('pp_pendingAdd');
      } catch (e) {}
      return;
    }
    // One-shot: clear immediately so a repeated failure can never auto-loop.
    try {
      sessionStorage.removeItem('pp_pendingAdd');
    } catch (e) {}

    const pf = document.querySelector('product-form');
    if (!pf) return;
    const idInput = pf.querySelector('[name="id"]');
    if (!idInput) return;
    idInput.value = pending.id;
    const qtyInput = pf.querySelector('[name="quantity"]');
    if (qtyInput && pending.qty) qtyInput.value = pending.qty;

    const submitBtn = pf.querySelector('[type="submit"]');
    if (!submitBtn) return;
    submitBtn.removeAttribute('disabled');
    submitBtn.removeAttribute('aria-disabled');
    // Mark this as an auto-add so a failure shows the second-stage message.
    window.__ppAutoAddInProgress = true;
    submitBtn.click();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => setTimeout(tryPendingAdd, 500));
  } else {
    setTimeout(tryPendingAdd, 500);
  }
})();
